import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import MainHeader from "../../shared/MainHeader";
import { useFormik } from "formik";
import * as Yup from "yup";
import ButtonCommon from "../../shared/ButtonCommon";
import SelectInput from "../../shared/SelectInput";
import DateInput from "../../shared/DateInput";
import { useSelector } from "react-redux";
import { GET, POST } from "../../api/ApiService";
import EncryptionHelper from "../../shared/EncryptionHelper";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import CaseSearchDropdown from "../../shared/CaseSearchDropdown";
import TextInput from "../../shared/TextInput";
import TextAreaInput from "../../shared/TextAreaInput";
// import SingleFileInput from "../../shared/SingleFileInput";

// Tweakable grace window (in hours) for allowing DARs from the immediately
// previous month during the first hours of the current month.
const PREVIOUS_MONTH_GRACE_HOURS = 24;

// Safer parsing for `<input type="datetime-local">` values.
// Avoids browser/date-string timezone quirks that can shift the month boundary.
const parseDateTimeLocal = (value) => {
  if (!value || typeof value !== "string") return new Date(NaN);

  const [datePart, timePart] = value.split("T");
  if (!datePart || !timePart) return new Date(NaN);

  const [year, month, day] = datePart.split("-").map((n) => Number(n));
  const [hour, minute, second = 0] = timePart.split(":").map((n) => Number(n));

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second)
  ) {
    return new Date(NaN);
  }

  return new Date(year, month - 1, day, hour, minute, second);
};

const CreateNewDar = () => {
  const userDetails = useSelector((state) => state.userReducer.userDetails);
  const [resetDropdown, setResetDropdown] = useState(false);
  const [caseDetails, setCaseDetails] = useState();
  const [billedHours, setBilledHours] = useState(0.0);
  // const [selectedFiles, setSelectedFiles] = useState();
  // const [clearFile, setClearFile] = useState(false);
  const [activityDropdown, setActvityDropdwon] = useState();
  // 🟩 New states for modals
  const [errorModal, setErrorModal] = useState({ open: false, message: "" });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: "",
    data: null,
  });

  useEffect(() => {
    if (resetDropdown) {
      setResetDropdown(false);
    }
  }, [resetDropdown]);

  // const handleFileChange = (files) => {
  //   setClearFile(false);
  //   setSelectedFiles(files);
  // };

  useEffect(() => {
    getActivityList();
  }, []);

  const getActivityList = async () => {
    try {
      const response = await GET("daily-activity/activity-list");
      response.sort((a, b) => a.value.localeCompare(b.value));
      setActvityDropdwon(response);
    } catch (err) {
      console.error("Error fetching activity list:", err?.response?.data);
      toast.error(err?.response?.data?.message);
    }
  };

  const getConsumedTime = (newValue) => {
    if (newValue) {
      GET(`daily-activity/total-consumed-time?caseID=${newValue?.CaseID}`)
        .then((response) => {
          const encryptedData = response?.encryptedData;
          const decryptedData = EncryptionHelper.decryptData(encryptedData);

          setBilledHours(0);
          if (decryptedData) {
            setBilledHours(Number(decryptedData.billed_hours).toFixed(2));
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
        });
    }
  };

  const formik = useFormik({
    initialValues: {
      case: null,
      activity: "",
      activityStart: "",
      activityEnd: "",
      subject: "",
      note: "",
    },
    validationSchema: Yup.object({
      case: Yup.object().required("Case is required"),
      activity: Yup.string().required("Activity is required"),
      activityStart: Yup.date()
        .required("Start date is required")
        .test(
          "is-within-24-hours",
          "Activity cannot be longer than 12 hours.",
          function (value) {
            const { activityEnd } = this.parent;
            return activityEnd
              ? value >=
                  new Date(activityEnd).setHours(
                    new Date(activityEnd).getHours() - 12
                  )
              : true;
          }
        ),
      activityEnd: Yup.date()
        .required("End date is required")
        .min(Yup.ref("activityStart"), "End date must be after start date"),
    }),

    onSubmit: (values, { resetForm }) => {
      if (caseDetails?.Status === "Closed") {
        toast.error(
          "This case is closed. Unable to create Daily Activity Report."
        );
        return;
      }

      if (caseDetails?.Status === "Suspended") {
        toast.error(
          "Unable to save Daily Activity Report because this case is suspended. Please refrain from performing any further work on this case and contact the Billing team to resolve the issue."
        );
        return;
      }

      const timeDifference = calculateTimeDifference(
        values.activityStart,
        values.activityEnd
      );

      // 🟥 Rule 1: Duration = 0 (same start and end)
      if (timeDifference === 0) {
        setErrorModal({
          open: true,
          message: "The Start and End dates must be different.",
        });
        return;
      }

      // 🟨 Rule 2: Duration > 5 hours (confirmation)
      if (timeDifference > 5) {
        setConfirmModal({
          open: true,
          message:
            "Are you sure you want to create an activity that is longer than 5 hours?",
          data: { values, resetForm },
        });
        return;
      }

      // Proceed normally if no special validation triggered
      handleSave(values, resetForm);
    },
  });

  const handleSave = (values, resetForm) => {
    const timeDifference = calculateTimeDifference(
      values.activityStart,
      values.activityEnd
    );

    const remainingHours = caseDetails?.HoursLimit - billedHours;

    if (timeDifference > remainingHours && caseDetails?.HoursLimit != null) {
      toast.warning(
        `The activity duration ${timeDifference.toFixed(
          2
        )} hours exceeds the billable hours limit for this case.`
      );
      return;
    }

    if (remainingHours < 10) {
      toast.warning(
        "Notice: This case has less than 10 billable hours remaining. Be sure to request a supplemental order if more hours are needed."
      );
    }

    const activityEndDate = parseDateTimeLocal(values.activityEnd);
    if (Number.isNaN(activityEndDate.getTime())) {
      toast.error(
        "Error: unable to create Daily Activity Report because the activity end time is invalid."
      );
      return;
    }
    const now = new Date();
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfPreviousMonth = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1
    );
    const monthGracePeriodEnd = new Date(
      startOfCurrentMonth.getTime() +
        PREVIOUS_MONTH_GRACE_HOURS * 60 * 60 * 1000
    );

    // Block creating DARs for dates in any previous month
    if (activityEndDate < startOfCurrentMonth) {
      const isWithinGracePeriodWindow = now < monthGracePeriodEnd;
      const isInImmediatelyPreviousMonth =
        activityEndDate >= startOfPreviousMonth;

      if (!(isWithinGracePeriodWindow && isInImmediatelyPreviousMonth)) {
        const formatDuration = (milliseconds) => {
          const totalMinutes = Math.floor(milliseconds / (1000 * 60));
          const days = Math.floor(totalMinutes / (60 * 24));
          const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
          const minutes = totalMinutes % 60;
          const parts = [];
          if (days) parts.push(`${days}d`);
          if (hours) parts.push(`${hours}h`);
          parts.push(`${minutes}m`);
          return parts.join(" ");
        };

        const gracePeriodElapsedMs = Math.max(
          0,
          now.getTime() - monthGracePeriodEnd.getTime()
        );
        const gracePeriodLabel = `Grace period: first ${PREVIOUS_MONTH_GRACE_HOURS} hours of the month (until ${monthGracePeriodEnd.toLocaleString()}).`;
        const graceElapsedLabel = `Time beyond grace period: ${formatDuration(
          gracePeriodElapsedMs
        )}.`;
        const reasonLabel = isWithinGracePeriodWindow
          ? "During the grace period, entries are only allowed for dates in the immediately previous month."
          : "The activity end time is in a previous month and the grace period has ended.";

        toast.error(
          `Error: unable to create Daily Activity Report. ${reasonLabel} ${gracePeriodLabel} ${graceElapsedLabel}`
        );
        return;
      }
    }

    const fortyFiveDaysAgoString = getDate45DaysAgoString(new Date());
    const fortyFiveDaysAgo = new Date(fortyFiveDaysAgoString);
    if (activityEndDate < fortyFiveDaysAgo) {
      toast.error(
        "Error: unable to create Daily Activity Report because the activity end time is more than 45 days in the past."
      );
      return;
    }

    

    const postData = {
      CaseID: values?.case?.CaseID,
      FinishTime: values.activityEnd,
      Exported: false,
      ServiceOrActivity: values.activity,
      StartTime: values.activityStart,
      CreatedByUserID: userDetails?.id,
      Subject: values.subject,
      Note: values.note,
    };

    console.log("data to be encrypted ==>", postData);

    const encryptData = EncryptionHelper.encryptData(postData);

    POST("daily-activity/create", { encryptData })
      .then((resp) => {
        toast.success(resp?.message);
        resetForm();
        setResetDropdown(true);
        setCaseDetails(null);
        setBilledHours(null);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  function calculateTimeDifference(startTime, finishTime) {
    const start = parseDateTimeLocal(startTime);
    const finish = parseDateTimeLocal(finishTime);
    const differenceInMilliseconds = finish - start;
    const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
    return differenceInHours;
  }

  const formatDateTime = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getDate45DaysAgoString = (dateInput) => {
    const baseDate = new Date(dateInput);
    const adjustedDate = new Date(baseDate);
    adjustedDate.setDate(adjustedDate.getDate() - 45);
    return formatDateTime(adjustedDate);
  };


  // 🟩 Handle confirmation proceed

  const handleProceedConfirm = () => {
    if (confirmModal.data) {
      handleSave(confirmModal.data.values, confirmModal.data.resetForm);
    }
    setConfirmModal({ open: false, message: "", data: null });
  };

  return (
    <Box>
      <MainHeader headingText="Create New DAR" />
      <Stack
        p={3}
        gap={4}
        sx={{
          border: "1px solid #C1C1C1",
          minHeight: "80vh",
        }}
      >
        <Stack gap={4}>
          <Stack gap={1}>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "700",
                lineHeight: "21.92px",
                textAlign: "left",
              }}
            >
              Create a new Daily Activity Report
            </Typography>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: "400",
                lineHeight: "18.27px",
                textAlign: "left",
              }}
            >
              ⓘ Select the case name, activity, set the start and end
              dates/times, and press submit.
            </Typography>
          </Stack>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>
              {/* Existing Field 1 */}
              <Grid item xs={12} sm={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Case<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Stack sx={{ width: "100%" }}>
                    <CaseSearchDropdown
                      name="case"
                      value={formik.values.case}
                      onChange={(event, newValue) => {
                        setCaseDetails(newValue);
                        formik.setFieldValue("case", newValue);
                        getConsumedTime(newValue);
                      }}
                      onBlur={formik.handleBlur}
                      reset={resetDropdown}
                      filtered={true}
                    />
                    {formik.touched.case && formik.errors.case ? (
                      <div
                        style={{
                          fontSize: "smaller",
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {formik.errors.case}
                      </div>
                    ) : null}
                  </Stack>
                </Stack>
              </Grid>

              {/* New Field: Subject */}
              {/* <Grid item xs={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Subject<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Stack sx={{ width: "100%" }}>
                    <TextInput
                      name="subject"
                      placeholder=""
                      onChangeValue={formik.handleChange}
                      onBlurValue={formik.handleBlur}
                      value={formik.values.subject}
                      startIcon={false}
                    />
                    {formik.touched.subject && formik.errors.subject ? (
                      <div
                        style={{
                          fontSize: "smaller",
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {formik.errors.subject}
                      </div>
                    ) : null}
                  </Stack>
                </Stack>
              </Grid> */}

              {/* Existing Field 2 */}
              <Grid item xs={12} sm={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Select Activity<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Stack sx={{ width: "100%" }}>
                    <SelectInput
                      name="activity"
                      placeholder=""
                      onChangeValue={formik.handleChange}
                      onBlurValue={formik.handleBlur}
                      value={formik.values.activity}
                      dropDownData={activityDropdown}
                      helperText=""
                    />
                    {formik.touched.activity && formik.errors.activity ? (
                      <div
                        style={{
                          fontSize: "smaller",
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {formik.errors.activity}
                      </div>
                    ) : null}
                  </Stack>
                </Stack>
              </Grid>

              {/* New Field: Note */}
              {/* <Grid item xs={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Note<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Stack sx={{ width: "100%" }}>
                    <TextAreaInput
                      name="note"
                      placeholder=""
                      onChangeValue={formik.handleChange}
                      onBlurValue={formik.handleBlur}
                      value={formik.values.note}
                      rows={10}
                      helperText=""
                    />
                    {formik.touched.note && formik.errors.note ? (
                      <div
                        style={{
                          fontSize: "smaller",
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {formik.errors.note}
                      </div>
                    ) : null}
                  </Stack>
                </Stack>
              </Grid> */}

              {/* Existing Fields (Activity Start/End) */}
              <Grid item xs={12} sm={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Activity Start Date & Time
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Stack sx={{ width: "100%" }}>
                    <DateInput
                      type="datetime-local"
                      name="activityStart"
                      placeholder="activityStart"
                      onChangeValue={(e) => {
                        const selectedDate = e.target.value;
                        formik.setFieldValue("activityStart", selectedDate);
                        formik.setFieldValue("activityEnd", selectedDate); // Set activityEnd to the same value
                      }}
                      onBlurValue={formik.handleBlur}
                      value={formik.values.activityStart}
                      helperText=""
                    />
                    {formik.touched.activityStart &&
                    formik.errors.activityStart ? (
                      <div
                        style={{
                          fontSize: "smaller",
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {formik.errors.activityStart}
                      </div>
                    ) : null}
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Activity End Date & Time
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Stack sx={{ width: "100%" }}>
                    <DateInput
                      type="datetime-local"
                      name="activityEnd"
                      placeholder="activityEnd"
                      onChangeValue={formik.handleChange}
                      onBlurValue={formik.handleBlur}
                      value={formik.values.activityEnd}
                      helperText=""
                    />
                    {formik.touched.activityEnd && formik.errors.activityEnd ? (
                      <div
                        style={{
                          fontSize: "smaller",
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {formik.errors.activityEnd}
                      </div>
                    ) : null}
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>Subject</Typography>
                  <Stack sx={{ width: "100%" }}>
                    <TextInput
                      name="subject"
                      placeholder=""
                      onChangeValue={formik.handleChange}
                      onBlurValue={formik.handleBlur}
                      value={formik.values.subject}
                      startIcon={false}
                    />
                    {formik.touched.subject && formik.errors.subject ? (
                      <div
                        style={{
                          fontSize: "smaller",
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {formik.errors.subject}
                      </div>
                    ) : null}
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>Note</Typography>
                  <Stack sx={{ width: "100%" }}>
                    <TextAreaInput
                      name="note"
                      placeholder=""
                      onChangeValue={formik.handleChange}
                      onBlurValue={formik.handleBlur}
                      value={formik.values.note}
                      rows={10}
                      helperText=""
                    />
                    {formik.touched.note && formik.errors.note ? (
                      <div
                        style={{
                          fontSize: "smaller",
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {formik.errors.note}
                      </div>
                    ) : null}
                  </Stack>
                </Stack>
              </Grid>

              {/* <Grid
                item
                xs={6}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <SingleFileInput
                  onFileChange={handleFileChange}
                  clearFile={clearFile}
                />
              </Grid> */}

              {/* Billing Information (If Available) */}
              {caseDetails && (
                <Grid item xs={12}>
                  <Stack>
                    <Typography sx={{ textAlign: "left" }}>
                      Billing hours limit for this Case:{" "}
                      {caseDetails?.HoursLimit ?? 0}
                    </Typography>
                    <Typography sx={{ textAlign: "left" }}>
                      Total hours billed: {billedHours}
                    </Typography>
                    <Typography sx={{ textAlign: "left" }}>
                      Hours remaining: {caseDetails?.HoursLimit - billedHours}
                    </Typography>
                  </Stack>
                </Grid>
              )}

              {/* Save Button */}
              <Grid item xs={12}>
                <ButtonCommon type="submit">Save</ButtonCommon>
              </Grid>
            </Grid>
          </form>
        </Stack>
      </Stack>

      {/* 🟥 Error Modal */}
      <Dialog
        open={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: "" })}
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography>{errorModal.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModal({ open: false, message: "" })}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🟨 Confirmation Modal */}
      <Dialog
        open={confirmModal.open}
        onClose={() =>
          setConfirmModal({ open: false, message: "", data: null })
        }
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <Typography>{confirmModal.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmModal({ open: false, message: "", data: null })
            }
          >
            Cancel
          </Button>
          <Button onClick={handleProceedConfirm} variant="contained">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateNewDar;
